import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Deliberately checks for explicit hiding rather than measuring layout.
 * `offsetWidth`/`getClientRects` are zero in any environment that does not
 * lay out (jsdom, some headless renderers), which would empty the trap and
 * silently disable it. The panel is unmounted when closed, so there is no
 * off-screen content to filter out anyway.
 */
function isHidden(element: HTMLElement): boolean {
  if (element.hasAttribute('hidden')) return true
  if (element.getAttribute('aria-hidden') === 'true') return true
  if (element.closest('[aria-hidden="true"]')) return true

  const style = element.ownerDocument.defaultView?.getComputedStyle(element)
  return style?.display === 'none' || style?.visibility === 'hidden'
}

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => !isHidden(element)
  )
}

/**
 * Reads the focused element, looking through shadow boundaries.
 * `document.activeElement` only ever reports the shadow host, so a naive
 * implementation would restore focus to the widget container rather than to
 * whatever on the host page opened it.
 */
function activeElementDeep(): HTMLElement | null {
  let active = document.activeElement as HTMLElement | null
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement as HTMLElement
  }
  return active
}

export interface FocusTrapOptions {
  /** Called when the user presses Escape. */
  onEscape?: () => void
}

/**
 * Traps keyboard focus inside `containerRef` while `active`.
 *
 * Without this, a keyboard user tabs straight out of the open dialog and into
 * the host page behind it — and an earlier version kept the panel in the DOM
 * when closed, so they could tab into an invisible booking form.
 *
 * The escape handler is held in a ref rather than being an effect dependency.
 * It is nearly always an inline arrow function, so a new identity arrives on
 * every render of the host component; depending on it meant this effect tore
 * down and set up again on every keystroke, and its setup moves focus to the
 * first control. The result was that typing one character in any field threw
 * focus back to the close button, so each further character needed a fresh
 * click. Focus must move only when the trap actually opens.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  options: FocusTrapOptions = {}
): void {
  const onEscapeRef = useRef(options.onEscape)

  useEffect(() => {
    onEscapeRef.current = options.onEscape
  }, [options.onEscape])

  useEffect(() => {
    if (!active) return
    const container = containerRef.current
    if (!container) return

    const previouslyFocused = activeElementDeep()

    // Move focus in on open. Prefer the first control; fall back to the
    // container itself so screen readers announce the dialog.
    const initial = focusableWithin(container)[0]
    if (initial) {
      initial.focus()
    } else {
      container.setAttribute('tabindex', '-1')
      container.focus()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onEscapeRef.current?.()
        return
      }

      if (event.key !== 'Tab') return

      const focusable = focusableWithin(container)
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const current = activeElementDeep()

      if (event.shiftKey && (current === first || !container.contains(current))) {
        event.preventDefault()
        last.focus()
        return
      }

      if (!event.shiftKey && current === last) {
        event.preventDefault()
        first.focus()
      }
    }

    // Capture phase: the host page may stop propagation on its own listeners.
    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      // isConnected, not document.contains: the opener may live in a shadow
      // root, which document.contains never reports.
      if (previouslyFocused?.isConnected) {
        previouslyFocused.focus()
      }
    }
    // Deliberately excludes the escape handler: see the note above.
  }, [active, containerRef])
}
